/**
 * Shared geometry for the bloodwork PDF, all of it in CAPTURE PIXELS — the
 * pixels html2canvas renders and html2pdf slices. One page is
 * PDF_PAGE_BREAK_GRID_PX (1073) of them at PDF_CAPTURE_WIDTH_PX (801) wide; see
 * the derivation in bloodwork-analysis-pdf.tsx.
 *
 * ONE UNIT, everywhere. The Insights panels measure themselves off the rendered
 * card, the bar-graph pair below was measured off a real export, and the page
 * capacities are read off that same page geometry. There was briefly a
 * conversion between the panels' pixels and the packer's older hand-tuned
 * scale; once the bar-graph pair was re-measured in capture pixels the two
 * scales became one, and the conversion silently became a 22% under-count.
 * Nothing here converts any more, which is the point — a second unit is what
 * made the arithmetic wrong twice.
 */

/**
 * A section's bar-graph card, in the packer's units: `BASE + markers * ROW`.
 *
 * MEASURED off the 2026-08-31 export, converted to capture pixels through the
 * page geometry the container derives (a page is PDF_PAGE_BREAK_GRID_PX tall at
 * PDF_CAPTURE_WIDTH_PX wide, so the conversion is arithmetic, not a guess):
 *
 *     markers   rendered   old estimate (180 + 40n)
 *        5         368            380     over
 *        7         484            460     UNDER
 *        9         561            540     UNDER
 *       14         861            740     UNDER by 121
 *
 * The old pair was calibrated on short sections and drifted short as they grew,
 * so the packer thought a fourteen-marker section ended 121px above where it
 * really did and placed what followed against a stale page position. The pair
 * below fits all four points and rounds long at every one of them.
 *
 * Shared because two places ask the same question: the packer in
 * bloodwork-analysis-pdf.tsx budgets pages with it, and pdf-section.tsx uses it
 * to decide whether a card fits under an Insights panel. They were separate
 * copies and had already drifted apart.
 */
export const BAR_GRAPH_BASE_HEIGHT = 100;
export const BAR_GRAPH_ROW_HEIGHT = 56;
/**
 * A text result prints as one row of labeled columns rather than a chart.
 *
 * 33px of content plus the 1px hairline that separates it from the row above
 * (`.ref-range-text-row-divider`). The FIRST row of a block has no row above
 * it and so pays no hairline, which makes this 1px generous for a block rather
 * than 1px short — the safe direction for a page budget.
 */
export const BAR_GRAPH_TEXT_ROW_HEIGHT = 34;
/**
 * ...and the block of them is ruled off above and below, which costs its 19px
 * ONCE however many rows there are. Both numbers are measured, not guessed.
 */
export const BAR_GRAPH_TEXT_BLOCK_HEIGHT = 19;

/**
 * The second line a text row grows when it prints the previous collection's
 * result under the current one.
 *
 * 2px margin over an 11px line at 1.3, rounded up: `.ref-range-text-previous`
 * in bloodwork-ref-range-analysis.css. It became a per-row question on
 * 2026-09-24, when that line stopped being printed only for results that had
 * CHANGED and started printing under every row that has a previous value. A
 * urinalysis of 34 text rows gains half a page from it, and a page budget that
 * does not know grows a section past the page it was packed onto.
 */
export const BAR_GRAPH_TEXT_PREVIOUS_LINE_HEIGHT = 17;

/**
 * How tall a block of text rows prints, previous-collection lines included.
 *
 * Takes the rows themselves rather than a count, because only the rows with a
 * previous result to show grow the extra line: a marker the last collection
 * never measured, and every row on a report with no previous collection at all,
 * costs exactly one row.
 */
/**
 * What ONE text row costs, which is not the same for all of them: a row that
 * prints the previous collection's result underneath is a line taller.
 *
 * Exported because a section that has to be SPLIT across pages needs the rows
 * one at a time — see pdfSectionRowPacking — while a section that fits needs
 * only the total below.
 */
export function textRowHeight(marker: {
  previousText?: string;
  isPreviousOnly?: () => boolean;
}): number {
  const printsPrevious =
    !!(marker?.previousText || '').trim()
    && !(marker?.isPreviousOnly && marker.isPreviousOnly());
  return (
    BAR_GRAPH_TEXT_ROW_HEIGHT
    + (printsPrevious ? BAR_GRAPH_TEXT_PREVIOUS_LINE_HEIGHT : 0)
  );
}

export function estimatedTextRowsHeight(
  textMarkers: {
    previousText?: string;
    isPreviousOnly?: () => boolean;
  }[] = [],
): number {
  if (!textMarkers.length) {
    return 0;
  }
  return (
    textMarkers.reduce((total, marker) => total + textRowHeight(marker), 0)
    + BAR_GRAPH_TEXT_BLOCK_HEIGHT
  );
}

/**
 * Does a section's results card share the page with its Insights panel?
 *
 * ONE ANSWER, because two places act on it and a disagreement between them is
 * an orphaned heading. pdf-section.tsx asks it to decide whether to print the
 * break between the panel and the card (and whether the card needs its title
 * repeated); the section packer in bloodwork-analysis-pdf.tsx asks it to know
 * how full that page already is before it puts the next section on it.
 *
 * They asked it separately, at different budgets, and gave different answers on
 * the same report: the packer budgets pages at 900 to hold slack back, so it
 * read Blood Oxygen's gauge and its seven charts as two pages when they had
 * printed as one, and let Iron Status onto a page that was already full. What
 * printed was the Iron heading at the foot of the page with its card on the
 * next one.
 *
 * The capacity here is the REAL page (a page holds PDF_PAGE_BREAK_GRID_PX of
 * captured content and a section opens with an h4 worth about 40 of it), not
 * the packer's deliberately short budget: this question is "do these two fit
 * together", where that slack is simply a wrong answer — at 900 it said no for
 * Stress & Adrenal, whose panel and results measure 871 and sit on one page
 * perfectly well.
 *
 * `barGraphsHeight` is the card WITHOUT its keep-together padding, which is to
 * say exactly what BASE + markers * ROW + estimatedTextRowsHeight gives.
 */
export const INSIGHTS_PAGE_CAPACITY = 1030;
/** Held back so a card predicted to fit really does, rather than just clearing. */
export const INSIGHTS_PAGE_SAFETY_BUFFER = 60;
/** .pdf-glucose-bar-graphs pads the top, to buffer a title moved to a new page. */
export const KEEP_TOGETHER_PADDING_TOP = 16;
/** The gap between an Insights panel and the card under it. */
export const INSIGHTS_SECTION_GAP = 24;

export function barGraphsShareInsightsPage(
  panelHeight: number,
  barGraphsHeight: number,
): boolean {
  // No panel printing means nothing to share a page WITH: the card opens the
  // page itself, and the caller's own packing decides what follows it.
  if (!(panelHeight > 0)) {
    return false;
  }
  return (
    panelHeight
      + INSIGHTS_SECTION_GAP
      + barGraphsHeight
      + KEEP_TOGETHER_PADDING_TOP
      + INSIGHTS_PAGE_SAFETY_BUFFER
    <= INSIGHTS_PAGE_CAPACITY
  );
}

/**
 * How wide a row of a note card's body is, and how wide the characters on it
 * are — MEASURED in a browser at the capture width rather than guessed, because
 * the guess this replaces (a flat 90 characters per row) over-counted a page of
 * real notes by a third and printed pages two-thirds full.
 *
 * Both note cards render through `renderNoteBody` into a `pre-wrap` block of
 * the same width, so they wrap at the same place. That block measures 703px;
 * the figure below is short of it because a wrap breaks at a word boundary and
 * leaves the right edge ragged, and because a **bold** run (which the gather
 * digest writes for every pattern title) sets wider than the body face.
 */
export const NOTE_ROW_WIDTH_PX = 640;
/**
 * Character widths at the body's 12px, measured across whole alphabets in the
 * card itself. A flat average is what made the old estimate unusable in both
 * directions at once: prose averages 5.3px a character while the same sentence
 * in capitals averages 7.2px, so one number is either 30% long on ordinary
 * notes or 30% short on a line the practitioner typed in caps.
 */
const CHAR_WIDTH_BODY = 6.0;
const CHAR_WIDTH_CAPITAL = 8.2;
const CHAR_WIDTH_DIGIT = 6.7;
const CHAR_WIDTH_WIDE = 10.5;
const CHAR_WIDTH_NARROW = 3.3;
/** Spaces and the thin letters and punctuation that set near a space's width. */
const NARROW_CHARS = " \t.,;:!|'\"()[]{}iljtfr-";
const WIDE_CHARS = 'mwMW@%';
/** A `## ` line renders as an h6, not as body text (18px box + its margins). */
export const NOTE_HEADING_HEIGHT = 40;
/**
 * A blank line INSIDE a block prints as an empty row, spaces and all, because
 * the block is `pre-wrap`. A blank line at a block's EDGE costs nothing:
 * `renderNoteBody` trims the blank lines off each block before rendering it, so
 * the blank a practitioner leaves between one section and the next heading is
 * not on the page at all. Charging both alike was worth ~18px a section — a
 * sixth of a page on a report with six sections' notes, spent on nothing.
 */
export const NOTE_BLANK_LINE_HEIGHT = 18;

/** Rendered width (capture px) of one line of a note's body text. */
export function noteTextWidth(line: string): number {
  let width = 0;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (NARROW_CHARS.indexOf(c) >= 0) width += CHAR_WIDTH_NARROW;
    else if (WIDE_CHARS.indexOf(c) >= 0) width += CHAR_WIDTH_WIDE;
    else if (c >= 'A' && c <= 'Z') width += CHAR_WIDTH_CAPITAL;
    else if (c >= '0' && c <= '9') width += CHAR_WIDTH_DIGIT;
    else width += CHAR_WIDTH_BODY;
  }
  return width;
}

/** How many visual rows one stored line of a note wraps onto. */
export function noteRowCount(line: string): number {
  return Math.max(1, Math.ceil(noteTextWidth(line) / NOTE_ROW_WIDTH_PX));
}

/**
 * Rough height (capture px) of a practitioner note card: its fixed overhead
 * plus its text, counted in WRAPPED ROWS.
 *
 * The wrap is the whole point. A note typed as one long paragraph with no
 * Returns is a single `\n`-delimited line but renders as dozens of rows, so
 * counting lines under-estimates it by an order of magnitude — the container
 * then reads the card as fitting in the space left on the last section's page
 * and it spills. Errs high; over-estimating only pushes the card to a fresh
 * page, which is the safe direction.
 *
 * Shared by the two trailing note cards (Section Notes and Insights), which had
 * separate copies of this arithmetic and had drifted: Section Notes counted
 * wrapped rows and Insights counted lines. `cardOverhead` and `lineHeight` stay
 * per-card because each card's chrome and body type differ.
 */
export function estimateNoteCardHeight(
  text: string,
  { cardOverhead, lineHeight }: { cardOverhead: number; lineHeight: number },
): number {
  if (!(text || '').trim()) return 0;
  return (
    cardOverhead +
    noteLineHeights(text.split('\n'), lineHeight).reduce((a, b) => a + b, 0)
  );
}

/**
 * Rendered height (capture px) of each stored line of a note, index-aligned
 * with the lines passed in: a heading, a blank, or body text in wrapped rows.
 *
 * A LIST rather than a per-line call because a blank line's cost depends on
 * where it sits — see NOTE_BLANK_LINE_HEIGHT — and only its neighbours can say.
 *
 * Shared so the pre-paginator that slices a long note card into pages
 * (pdfNoteCardPagination) measures a line exactly the way the estimate does. A
 * card sliced on one arithmetic and budgeted on another is how a card ends up
 * believing it fits a page it runs past.
 */
export function noteLineHeights(lines: string[], lineHeight: number): number[] {
  const isHeading = (l: string) => (l || '').trim().startsWith('## ');
  const isBlank = (l: string) => (l || '').trim() === '';
  // A blank is trimmed away when every line between it and the block's edge —
  // the nearest heading, or the text's own end — is blank too.
  const atBlockEdge = (i: number): boolean => {
    let j = i - 1;
    while (j >= 0 && isBlank(lines[j])) j--;
    if (j < 0 || isHeading(lines[j])) return true;
    let k = i + 1;
    while (k < lines.length && isBlank(lines[k])) k++;
    return k >= lines.length || isHeading(lines[k]);
  };

  return lines.map((line, i) => {
    if (isHeading(line)) return NOTE_HEADING_HEIGHT;
    if (isBlank(line)) return atBlockEdge(i) ? 0 : NOTE_BLANK_LINE_HEIGHT;
    return noteRowCount(line) * lineHeight;
  });
}

/**
 * The Immune Activity panel's height in the PDF.
 *
 * Flat, unlike the self-estimating panels: WBCEvaluation plus its
 * considerations is the same shape whatever it finds.
 */
export const WBC_INSIGHTS_PDF_HEIGHT = 300;

/**
 * The Blood Oxygen panel's height in the PDF.
 *
 * MEASURED off the rendered panel at its real 300px width, in the same capture
 * pixels as everything else. A bare panel measures 383: card chrome 93, the
 * 200px gauge, and the size/colour row at 90 with its 10px margin. Each finding
 * AnemiaStatus prints adds a bordered box.
 *
 * Lives here rather than inside the PDF section's render because two callers
 * need it: that render, to decide whether the bar graphs fit under the panel,
 * and the section packer, to know what a Blood Oxygen page costs. A section the
 * packer cannot cost is a section whose page breaks have to be hardcoded, which
 * is what kept Iron Status on a page of its own.
 */
export function estimatedRbcInsightsPdfHeight(ba: any): number {
  const LINE = 18; // Bootstrap's 1.5 line-height at 12px
  const BOX = 10 + 16; // a bordered box: 10px margin, 3px border, 5px padding
  let height = 93 + 200 + 10 + 90;
  if (ba?.nutrientDeficiencyOrInflammationLikelyText) {
    height += BOX + LINE;
  }
  if (ba?.boneMarrowAnalysis?.boneMarrowOverproductionIsLikely) {
    height += BOX + LINE;
  }
  const rootCauses = ba?.possibleRootCauses?.() ?? [];
  if (rootCauses.length) {
    // Title line, then a line per cause — carried at two, because these read as
    // full sentences and wrap inside a 300px panel more often than not
    // ("Vitamin B12 Insufficiency Possibly Caused by Stomach Acid
    // Insufficiency" is three).
    height += BOX + LINE + rootCauses.length * 2 * LINE;
  }
  if (ba?.isAnyDehydrationPossibleOrHigher?.()) {
    height += BOX + LINE;
  }
  if (ba?.isPoorOxygenationPossibleOrHigher?.()) {
    height += BOX + LINE;
  }
  if (ba?.isPoorOxygenationDueToSmoking?.()) {
    height += BOX + LINE;
  }
  return height;
}
